import { useState, type FormEvent } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@/shared/snackbar/SnackbarContext';
import { useCreateVehicle, useUpdateVehicle } from '../api';
import { VIN_PATTERN, type VehicleDetail } from '../types';

/** Create (vehicle = null) or edit. The page mounts it only while open, so the fields start fresh every time. */
export function VehicleFormDialog({
    open,
    vehicle,
    onClose,
}: {
    open: boolean;
    vehicle: VehicleDetail | null;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    const { showError, showSuccess } = useSnackbar();
    const create = useCreateVehicle();
    const update = useUpdateVehicle();
    const busy = create.isPending || update.isPending;

    const [licensePlate, setLicensePlate] = useState(vehicle?.licensePlate ?? '');
    const [vin, setVin] = useState(vehicle?.vin ?? '');
    const [notes, setNotes] = useState(vehicle?.notes ?? '');
    const vinLocked = vehicle !== null && vehicle.mercedesStatus !== 'NONE';

    function onSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        const fields = { licensePlate: licensePlate.trim(), notes: notes.trim() || null };
        const input = { ...fields, vin };
        const options = {
            onSuccess: () => {
                showSuccess(t('vehicles.form.saved'));
                onClose();
            },
            onError: showError,
        };
        if (vehicle === null) {
            create.mutate(input, options);
        } else {
            update.mutate({ id: vehicle.id, ...(vinLocked ? fields : input) }, options);
        }
    }

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
            <form onSubmit={onSubmit}>
                <DialogTitle>
                    {vehicle === null ? t('vehicles.form.createTitle') : t('vehicles.form.editTitle')}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} sx={{ pt: 1 }}>
                        {vehicle === null && <DialogContentText>{t('vehicles.form.createHint')}</DialogContentText>}
                        <TextField
                            label={t('vehicles.fields.licensePlate')}
                            value={licensePlate}
                            // Stored uppercase by the BE; show it that way while typing.
                            onChange={(e) => setLicensePlate(e.target.value.toUpperCase())}
                            required
                            autoFocus
                            slotProps={{ htmlInput: { maxLength: 255, spellCheck: false } }}
                        />
                        <TextField
                            label={t('vehicles.fields.vin')}
                            value={vin}
                            onChange={(e) => setVin(e.target.value.toUpperCase().replace(/\s/g, ''))}
                            required
                            disabled={vinLocked}
                            helperText={vinLocked ? t('vehicles.form.vinLocked') : t('vehicles.form.vinHint')}
                            slotProps={{ htmlInput: { maxLength: 17, pattern: VIN_PATTERN, spellCheck: false } }}
                        />
                        <TextField
                            label={t('vehicles.form.notesOptional')}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            slotProps={{ htmlInput: { maxLength: 255 } }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={busy}>
                        {t('common.cancel')}
                    </Button>
                    <Button type="submit" variant="contained" color="secondary" disabled={busy}>
                        {vehicle === null ? t('vehicles.form.create') : t('common.save')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
